import * as React from 'react';
import classNames from 'classnames';
import {SVGSource} from '@shopify/images';
import style from './banner.scss';
import {
  Text,
  Flex,
  Icon,
  TextType,
  IconType,
  RichIcon,
  RichIconType,
} from '..';

export type BannerDisplayType = 'primary' | 'secondary' | 'warning' | 'sparse';

type DivElementProps = JSX.IntrinsicElements['div'];

function BannerNOOP() { return ''; }

export interface BannerProps extends DivElementProps {
  icon?: IconType;
  iconSource?: SVGSource;
  richIcon?: RichIconType;
  richIconSource?: string;
  renderIcon?: () => React.ReactNode;
  iconAlt?: string;
  iconSize?: number;
  display?: BannerDisplayType;
  text?: string;
  textType?: TextType;
}

const DEFAULT_ICON_SIZE = 40;

function getIconFill(display: BannerDisplayType) {
  switch (display) {
    case 'primary':
      return '#000000';
    case 'warning':
    case 'secondary':
      return '#603276';
  }

  return '#000000';
}

export function Banner({
  icon,
  iconSource,
  richIcon,
  richIconSource,
  renderIcon = BannerNOOP,
  iconAlt = '',
  iconSize = DEFAULT_ICON_SIZE,
  display = 'primary',
  text,
  textType,
  children,
  className,
  ...restProps
}: BannerProps) {
  const useRenderIcon = (renderIcon !== BannerNOOP);
  const showIcon = (!useRenderIcon && (Boolean(icon) || Boolean(iconSource)));
  const showRichIcon = (!useRenderIcon && !showIcon && (Boolean(richIcon) || Boolean(richIconSource)));

  return (
    <Flex
      alignItems="center"
      className={classNames(style.banner, style[display], className)}
      {...restProps}
    >
      {useRenderIcon && renderIcon()}
      {showIcon && (
        <Icon
          alt={iconAlt}
          fill={getIconFill(display)}
          icon={icon}
          size={iconSize}
          sourceSVG={iconSource}
          className={style.icon}
        />)}
      {showRichIcon && (
        <RichIcon
          alt={iconAlt}
          icon={richIcon}
          size={iconSize}
          source={richIconSource}
          className={style.icon}
        />)}
        {text
          ? <Text inheritColor style={{marginBottom: 0}} type={textType} text={text} />
          : children
        }
    </Flex>
  );
}

export default Banner;
